/* Copyright (c) 2020-present CloudIO Inc */

import { isNotEmpty } from 'componentregistry/utils/isEmpty';
import { Platform } from '../../platform/Platform';
import {
  AliasType,
  ComponentManagerFn,
  ViewAttributeChooserProp,
} from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { baseComponentManager } from '../../registry/Components';
import { analyzeSelect } from '../Select/SelectAnalyze';
import { baseFieldProps, fontStyleProps } from '../TextField/BaseField';
import { defaultValue } from './AsyncSelect';
import { Select } from '../Select/Select';

const AsyncSelectManager: ComponentManagerFn<'AsyncSelect'> = (
  platform: Platform,
) => {
  return {
    ...baseComponentManager<'AsyncSelect'>(platform, 'AsyncSelect'),
    type: 'AsyncSelect',
    name: 'Async Select',
    isPropertyVisible(prop, itemData): boolean {
      switch (prop) {
        case 'valueAttribute':
        case 'labelAttribute':
          return isNotEmpty(itemData.comboDatasourceAlias);
        case 'delimiter':
          return itemData.multi;
        case 'colorExpression':
        case 'bgColorExpression':
        case 'specialCharacters':
          return !!itemData.ignoreSpecialCharacters;
        default:
          return true;
      }
    },
    isChildAllowed: (page, item, childType) =>
      (
        [
          'SelectReturnValueMapping',
          'TriggerOnChange',
          'TriggerOnClick',
          'TriggerAfterServerValidate',
        ] as ItemType[]
      ).includes(childType),
    defaultValue,
    props: {
      ...baseFieldProps,
      ...fontStyleProps,
      viewAttribute: {
        ...(baseFieldProps.viewAttribute as ViewAttributeChooserProp),
        getAttributeDataTypes: (props) => {
          const multi = (props.props as Required<Select>).multi;
          return multi
            ? ['String']
            : [
                'Decimal',
                'Double',
                'Email',
                'Integer',
                'Reference',
                'String',
                'UserID',
                'YN',
                'TF',
              ];
        },
      },
      displayAttribute: {
        label: 'Display Attribute',
        type: 'ViewAttributeChooser',
        category: 'Data',
        aliasType: 'STORE',
        getAttributeDataTypes: () => ['String', 'Integer', 'Decimal', 'Double'],
        required: true,
      },
      comboDatasourceAlias: {
        label: 'Data Store Alias',
        type: 'DataSourceAliasChooser',
        category: 'Select Options',
        allowedTypes: ['CloudDataStore', 'ReadOnlyDataStore'],
        required: true,
      },
      valueAttribute: {
        label: 'Value Attribute',
        type: 'ComboAttributeChooser',
        aliasType: 'LOV',
        category: 'Select Options',
        required: true,
        getAttributeDataTypes: () => 'ALL',
        helpText:
          'The selected value from this attribute will be copied to the base View Attribute selected above.',
      },
      labelAttribute: {
        aliasType: 'LOV',
        getAttributeDataTypes: () => 'ALL',
        label: 'Label Attribute',
        category: 'Select Options',
        required: true,
        type: 'ComboAttributeChooser',
        helpText:
          'The selected value from this attribute will be copied to the base Display Attribute selected above.',
      },
      minFilterChars: {
        label: 'Minimum Filter Characters',
        type: 'number',
        category: 'Select Options',
        helpText: 'Minimum number of characters to be entered before querying',
      },
      placeholder: {
        label: 'Placeholder',
        type: 'string',
      },
      unknownValueMessage: {
        label: 'Unknown Value Message',
        type: 'expression',
        returnType: async () => 'string',
        helpText: 'Unknown Value Error Message',
        category: 'Data',
      },
      multi: {
        label: 'Multi Selection',
        type: 'boolean',
        category: 'Select Options',
      },
      delimiter: {
        label: 'Delimiter',
        type: 'string',
        category: 'Select Options',
      },
      selectOptionOnBlur: {
        label: 'Select Option On Blur',
        type: 'boolean',
        category: 'Select Options',
        helpText: 'Enable the option to select the topmost option on Blur.',
      },
      selectOptionOnTab: {
        label: 'Select Option On Tab',
        type: 'boolean',
        category: 'Select Options',
        helpText: 'Enable the option to select the topmost option on Tab.',
      },
      serverValidateOnChange: {
        label: 'Server Validate On Change',
        type: 'boolean',
        category: 'Data',
      },
      ignoreSpecialCharacters: {
        label: 'Ignore Special Characters',
        type: 'boolean',
        helpText: 'Enable this to ignore special characters while searching.',
        category: 'Data',
      },
      specialCharacters: {
        label: 'Special Characters',
        type: 'string',
        helpText:
          'Enter special characters to exclude during the search. Use a semicolon (;) as the delimiter.',
        category: 'Data',
      },
    },
    getDataSourceAliasProperty: (aliasType: AliasType) =>
      aliasType === 'LOV' ? { aliasProp: 'comboDatasourceAlias' } : undefined,
    isDataSourceAliasUsed: (item, alias) =>
      item.props.comboDatasourceAlias === alias,
    category: 'Forms',
    getDisplayText: (item) => item.props.viewAttribute,
    icon: { iconName: 'io-select', iconType: 'cloudio' },
    excludeChildren: true,
    replaceableWith: ['TextField'],
    analyze: analyzeSelect,
    description: `**Overview**<br/>
The Async Select is a high-performance form component designed for selecting records from massive datasets (e.g., 10,000+ items).
Unlike the standard Select component, which downloads all options to the browser immediately, the Async Select employs a Lazy Loading strategy. It queries the server dynamically as the user types, making it the mandatory choice for "Search-as-you-type" fields like "Customer Search," "Part Number Lookup," or "Global User Directory."

**1. Data Binding (The Dual-Binding Requirement)**<br/>
To ensure instant page rendering without waiting for server lookups, this component requires a Dual Binding setup on your form's Data Store.
* **View Attribute (The ID):** Purpose: Stores the unique identifier (Foreign Key) of the selected item. Usage: This is the value sent to the backend when the form is saved (e.g., "customer_id").
* **Display Attribute (The Label):** Purpose: Stores the human-readable text associated with that ID. Usage: This allows the form to immediately display "Acme Corp" when loaded, even before the component has queried the server to resolve ID 501.
* **Requirement:** This attribute must be populated in your main Data Store (usually via a SQL JOIN on the server side).

**2. Data Source Configuration**<br/>
You must define the external source that provides the search results.
* **Data Store Alias:** Select the ReadOnly Data Store or Cloud Data Store that contains the master list of options.
* **Value Attribute (Remote):** The field in the external data store to use as the ID (e.g., "id").
* **Label Attribute (Remote):** The field in the external data store to use as the display text (e.g., "company_name").

**3. Performance & Tuning**<br/>
Since this component triggers network requests, you can tune its sensitivity to prevent database overloading.
* **Minimum Filter Characters:** The number of characters a user must type before the query executes. (Recommendation: Set to 3 or 4 for very large datasets to prevent broad queries like "A" or "The").
* **Unknown Value Message:** Custom text displayed if the form loads an ID (e.g., 999) that does not exist in the remote Data Store (e.g., "Unknown Customer").

**4. Multi-Selection**<br/>
You can configure the field to accept multiple values.
* **Multi Selection:**
    * **Enabled:** The user can search for and select multiple independent items.
    * **Storage:** The selected IDs are concatenated into a single string in the View Attribute.
    * **Delimiter:** The character used to separate the values in the database string. (Default: "~", e.g., "101~102~103"). Note: Ensure the chosen delimiter does not appear in your actual data IDs.

**5. User Experience (UX)**<br/>
* **Select Option On Blur / Tab:**
    * **Enabled:** If the user types a partial match (e.g., "Micro") and leaves the field (via Tab or Click), the component automatically selects the top-most result ("Microsoft"). This enables rapid data entry.
* **Placeholder:** Helper text shown when the field is empty (e.g., "Type to search...").

**6. Advanced Logic**<br/>
* **Select Return Value Mapping:** You can add this child component to map additional data from the selected record into other fields on your form. (Example: When a user selects a "Product," automatically fill the "Unit Price" and "Tax Rate" fields in the form).
* **Server Validate On Change:** Triggers a backend validation workflow immediately upon selection.`,
  };
};

export default AsyncSelectManager;
